import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, ExternalLink, Pencil, Eye, EyeOff, Ban, Search, Star, RotateCw } from "lucide-react";
import { adminListCases, adminSetStatus, findMoreLikeThis, adminSetHero, getHeroRotation, setHeroRotation, rotateHeroNow } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import AdminNav from "@/components/AdminNav";
import { toast } from "sonner";

const STATUS_TABS = ["All", "Published", "Draft", "Archived", "Rejected"];
const statusStyle = {
  Published: "bg-brlime text-ink", Draft: "bg-bryellow text-ink", Archived: "bg-secondary text-ink/60", Rejected: "bg-brorange text-white",
};

export default function AdminDashboard() {
  const { admin } = useAuth();
  const [cases, setCases] = useState([]);
  const [tab, setTab] = useState("All");
  const [loading, setLoading] = useState(true);
  const [rotation, setRotation] = useState(null);

  const load = () => {
    setLoading(true);
    adminListCases(tab === "All" ? undefined : tab).then((d) => { setCases(d); setLoading(false); });
  };
  useEffect(load, [tab]);
  useEffect(() => { getHeroRotation().then(setRotation); }, []);

  const toggleRotation = async () => {
    const res = await setHeroRotation(!rotation?.enabled);
    setRotation((r) => ({ ...r, enabled: res.enabled }));
    toast.success(res.enabled ? "Hero auto-rotation ON (every 12h)" : "Hero auto-rotation OFF — manual pin stays");
  };

  const doRotateNow = async () => {
    const res = await rotateHeroNow();
    if (res.rotated) { toast.success(`Hero rotated to ${res.slug}`); getHeroRotation().then(setRotation); load(); }
    else toast.message(res.reason || "Nothing to rotate");
  };

  const changeStatus = async (slug, status) => {
    await adminSetStatus(slug, status);
    toast.success(`Marked as ${status}`);
    load();
  };

  const toggleHero = async (slug) => {
    const res = await adminSetHero(slug);
    toast.success(res.hero ? "Set as landing hero" : "Removed as landing hero");
    load();
  };

  return (
    <div>
      <AdminNav />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="eyebrow text-ink/60">Editorial CMS</p>
          <h1 className="font-display font-extrabold text-4xl display-tight uppercase">Case Studies</h1>
          <p className="text-sm text-ink/50 mt-1">Signed in as {admin?.email}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 border-2 border-ink px-3 h-11" data-testid="hero-rotation-control" title="Auto-rotate the landing hero every 12h through Featured cases">
            <Star size={15} className={rotation?.enabled ? "text-cobalt" : "text-ink/30"} fill={rotation?.enabled ? "currentColor" : "none"} />
            <span className="text-sm font-semibold hidden sm:inline">Hero rotation</span>
            <button onClick={toggleRotation} data-testid="hero-rotation-toggle" className={`eyebrow px-2 py-1 ${rotation?.enabled ? "bg-brlime text-ink" : "bg-secondary text-ink/60"}`}>{rotation?.enabled ? "ON · 12h" : "OFF"}</button>
            <button onClick={doRotateNow} data-testid="hero-rotate-now" title="Rotate now" className="h-7 w-7 flex items-center justify-center border border-ink/20 hover:bg-cobalt hover:text-white"><RotateCw size={13} /></button>
          </div>
          <Link to="/admin/cases/new" data-testid="admin-add-case-button" className="group btn-tactile inline-flex items-center gap-2 bg-ink text-white font-bold px-5 h-11 hover:bg-cobalt">
            <Plus size={18} /> Add Case
          </Link>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-8">
        {STATUS_TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t.toLowerCase()}`}
            className={`btn-tactile px-4 h-9 border-2 border-ink font-semibold text-sm ${tab === t ? "bg-ink text-white" : "bg-white hover:bg-bryellow"}`}>
            {t}
          </button>
        ))}
      </div>

      <div className="mt-6 border-2 border-ink overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]" data-testid="admin-cases-table">
          <thead>
            <tr className="bg-ink text-white text-left">
              <th className="px-4 py-3 font-semibold">Company</th>
              <th className="px-4 py-3 font-semibold">Headline</th>
              <th className="px-4 py-3 font-semibold">Type</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-ink/40">Loading…</td></tr>
            ) : cases.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-ink/40">No cases in this view.</td></tr>
            ) : cases.map((c) => (
              <tr key={c.slug} className="border-t border-ink/10 hover:bg-secondary/60" data-testid={`admin-row-${c.slug}`}>
                <td className="px-4 py-3 font-bold whitespace-nowrap">{c.company}{c.featured && <span className="ml-2 text-xs text-brorange">★</span>}{c.hero && <span className="ml-2 eyebrow bg-cobalt text-white px-1.5 py-0.5 text-[10px]" data-testid={`hero-badge-${c.slug}`}>HERO</span>}</td>
                <td className="px-4 py-3 max-w-xs truncate">{c.headline}</td>
                <td className="px-4 py-3 text-ink/60 whitespace-nowrap">{c.business_type}</td>
                <td className="px-4 py-3"><span className={`eyebrow px-2 py-1 ${statusStyle[c.status] || "bg-secondary"}`}>{c.status}</span></td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1.5">
                    {c.status === "Published" && <a href={`/case/${c.slug}`} target="_blank" rel="noreferrer" title="View" className="h-8 w-8 flex items-center justify-center border border-ink/20 hover:bg-white"><ExternalLink size={14} /></a>}
                    {c.status === "Published" && <button onClick={() => toggleHero(c.slug)} title={c.hero ? "Remove landing hero" : "Set as landing hero"} data-testid={`set-hero-${c.slug}`} className={`h-8 w-8 flex items-center justify-center border border-ink/20 hover:bg-cobalt hover:text-white ${c.hero ? "bg-cobalt text-white" : ""}`}><Star size={14} fill={c.hero ? "currentColor" : "none"} /></button>}
                    {c.status === "Published" && <button onClick={async () => { await findMoreLikeThis(c.slug); toast.success("Searching for similar transformations — check the Queue shortly"); }} title="Find more like this" data-testid={`find-more-${c.slug}`} className="h-8 w-8 flex items-center justify-center border border-ink/20 hover:bg-cobalt hover:text-white"><Search size={14} /></button>}
                    <Link to={`/admin/cases/${c.slug}`} title="Edit" data-testid={`admin-edit-${c.slug}`} className="h-8 w-8 flex items-center justify-center border border-ink/20 hover:bg-white"><Pencil size={14} /></Link>
                    {c.status !== "Published"
                      ? <button onClick={() => changeStatus(c.slug, "Published")} title="Publish" data-testid={`admin-publish-${c.slug}`} className="h-8 w-8 flex items-center justify-center border border-ink/20 hover:bg-brlime"><Eye size={14} /></button>
                      : <button onClick={() => changeStatus(c.slug, "Draft")} title="Unpublish" data-testid={`admin-unpublish-${c.slug}`} className="h-8 w-8 flex items-center justify-center border border-ink/20 hover:bg-bryellow"><EyeOff size={14} /></button>}
                    <button onClick={() => changeStatus(c.slug, "Rejected")} title="Reject" data-testid={`admin-reject-${c.slug}`} className="h-8 w-8 flex items-center justify-center border border-ink/20 hover:bg-brorange hover:text-white"><Ban size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  );
}
